import { useEffect, useState } from "react";
import {
  cancelDirective,
  fetchDirectives,
  fetchTuning,
  postDirective,
  postTuning,
  timerAction,
} from "../lib/api";
import { Pause, Play, RotateCcw, Square, X } from "lucide-react";

const fmt = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

const TUNE_FIELDS = [
  ["evidence_saturation", "Evidence saturation", "stability-adjusted control items for a full unlock"],
  ["min_control_items", "Min control items", "hard floor regardless of confidence"],
  ["min_control_types", "Min control types", "of the 7 instrument types"],
  ["coverage_turns_per_category", "Turns per category", "coverage floor multiplier"],
  ["stall_threshold_seconds", "Stall threshold", "seconds of silence before a stall is flagged"],
  ["min_facets", "Min facets", "emergent sub-needs that must be named before the gate opens"],
  ["min_facet_parents", "Min facet spread", "distinct parent categories those facets must span"],
];

/** Operator-only: session clock, forced next question, and unlock tuning. */
export const OperatorControls = ({ subject, timer, onChanged }) => {
  const [dirs, setDirs] = useState(null);
  const [tune, setTune] = useState(null);
  const [draft, setDraft] = useState({
    probe: "",
    category: "",
    instruction: "",
    persist: false,
  });
  const [tdraft, setTdraft] = useState({});
  const [adjust, setAdjust] = useState(60);
  const [msg, setMsg] = useState("");

  const reload = async () => {
    const [d, t] = await Promise.all([fetchDirectives(subject), fetchTuning(subject)]);
    setDirs(d);
    setTune(t);
    setTdraft(t.tuning);
  };

  useEffect(() => {
    reload().catch(() => {});
  }, [subject]); // eslint-disable-line react-hooks/exhaustive-deps

  const clock = async (action, extra = {}) => {
    setMsg("");
    try {
      await timerAction({ action, subject, ...extra });
      await reload();
      onChanged?.();
      setMsg(`clock ${action}`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "clock change failed");
    }
  };

  const queue = async () => {
    setMsg("");
    try {
      const body = { subject };
      if (draft.probe) body.probe = draft.probe;
      if (draft.category) body.category = draft.category;
      if (draft.instruction.trim()) body.instruction = draft.instruction.trim();
      if (draft.persist && draft.probe) body.persist = true;
      await postDirective(body);
      setDraft({ probe: "", category: "", instruction: "", persist: false });
      await reload();
      setMsg(
        body.persist
          ? "standing order queued — it holds until that probe actually fires"
          : "override queued for the next turn"
      );
    } catch (e) {
      setMsg(e?.response?.data?.detail || "could not queue");
    }
  };

  const saveTuning = async (patch) => {
    setMsg("");
    try {
      const res = await postTuning({ subject, ...patch });
      setTune((t) => ({ ...t, ...res }));
      setTdraft(res.tuning);
      onChanged?.();
      setMsg(
        `saved · C now ${res.metrics.predictive_confidence}% · gate ${
          res.metrics.gate_open ? "OPEN" : "shut"
        } · ${res.forecast.turns_remaining} turns left`
      );
    } catch (e) {
      setMsg(e?.response?.data?.detail || "could not save");
    }
  };

  const fc = tune?.forecast;
  const pending = dirs?.pending || [];

  return (
    <div className="space-y-10" data-testid="admin-control-panel">
      {/* ---------------------------------------------------- clock */}
      <section>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <p className="label">Session clock</p>
          <span className="font-data text-[10px] text-zinc-600">
            you and the Cartographer can both drive this · every change is logged with its actor
          </span>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 border border-[var(--line)] px-4 py-4">
          <span
            data-testid="admin-timer-state"
            className={`font-data text-[11px] uppercase tracking-[0.18em] ${
              timer?.state === "running" ? "text-zinc-100" : "text-zinc-500"
            }`}
          >
            {timer?.state || "—"}
          </span>
          <span className="font-data text-xl tabular-nums text-zinc-100">
            {fmt(timer?.elapsed_seconds || 0)}
          </span>
          <span className="label">of {fmt(timer?.window_seconds || 28800)}</span>
          <div className="h-[3px] w-28 bg-[var(--line)]">
            <div
              className="h-full bg-zinc-200"
              style={{
                width: `${Math.min(100, ((timer?.elapsed_seconds || 0) / (timer?.window_seconds || 28800)) * 100)}%`,
              }}
            />
          </div>
          {timer?.expired && <span className="label !text-zinc-200">WINDOW EXPIRED</span>}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {[
              ["resume", "Start", Play, "admin-timer-start"],
              ["pause", "Pause", Pause, "admin-timer-pause"],
              ["stop", "Stop", Square, "admin-timer-stop"],
              ["reset", "Reset", RotateCcw, "admin-timer-reset"],
            ].map(([action, label, Icon, tid]) => (
              <button
                key={action}
                data-testid={tid}
                onClick={() =>
                  action === "reset"
                    ? window.confirm("Reset the clock to zero for this subject?") && clock(action)
                    : clock(action)
                }
                className="btn-stark flex items-center gap-2 !px-3 !py-2"
              >
                <Icon size={11} strokeWidth={1.8} />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="label">Adjust window</span>
          <input
            type="number"
            data-testid="admin-timer-adjust-input"
            value={adjust}
            onChange={(e) => setAdjust(Number(e.target.value))}
            className="w-24 border border-[var(--line-hi)] bg-transparent px-3 py-2 font-data text-[11px] text-zinc-100 outline-none"
          />
          <span className="label">minutes</span>
          <button
            data-testid="admin-timer-extend-btn"
            onClick={() => clock("adjust", { seconds: adjust * 60 })}
            className="btn-stark !px-3 !py-2"
          >
            Add
          </button>
          <button
            data-testid="admin-timer-reduce-btn"
            onClick={() => clock("adjust", { seconds: -adjust * 60 })}
            className="btn-stark !px-3 !py-2"
          >
            Remove
          </button>
          <button
            onClick={() => clock("adjust", { window_seconds: 8 * 3600 })}
            className="label hover:text-zinc-200"
          >
            reset to 8h
          </button>
        </div>

        {(timer?.events || []).length > 0 && (
          <ul className="mt-4 space-y-1" data-testid="admin-timer-log">
            {[...timer.events].reverse().slice(0, 6).map((e) => (
              <li key={`${e.t}-${e.action}`} className="font-data text-[10px] text-zinc-600">
                {e.t?.slice(11, 19)} · {e.action}
                {e.seconds ? ` ${e.seconds > 0 ? "+" : ""}${Math.round(e.seconds / 60)}m` : ""} ·{" "}
                {e.actor}
                {e.reason ? ` · “${e.reason}”` : ""}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="hair" />

      {/* ---------------------------------------------------- pacing override */}
      <section>
        <p className="label">Force the next question</p>
        <p className="mt-2 font-data text-[10px] text-zinc-600">
          mandatory on the Cartographer's very next turn, disguised in the conversation · the
          subject is never told
        </p>

        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_2fr_auto]">
          <select
            data-testid="admin-directive-probe"
            value={draft.probe}
            onChange={(e) => setDraft({ ...draft, probe: e.target.value })}
            className="border border-[var(--line-hi)] bg-[#0a0a0d] px-3 py-2 font-data text-[11px] text-zinc-100 outline-none"
          >
            <option value="">probe type — any</option>
            {(dirs?.probe_types || []).map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <select
            data-testid="admin-directive-category"
            value={draft.category}
            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            className="border border-[var(--line-hi)] bg-[#0a0a0d] px-3 py-2 font-data text-[11px] text-zinc-100 outline-none"
          >
            <option value="">category — any</option>
            {(dirs?.categories || []).map((c) => (
              <option key={c.id} value={c.id}>
                {`T${c.tier} · ${c.name}`}
              </option>
            ))}
          </select>

          <input
            data-testid="admin-directive-instruction"
            value={draft.instruction}
            onChange={(e) => setDraft({ ...draft, instruction: e.target.value })}
            placeholder="or write the instruction yourself"
            className="border border-[var(--line-hi)] bg-transparent px-3 py-2 text-xs text-zinc-100 outline-none placeholder:text-zinc-700"
          />

          <button
            data-testid="admin-directive-submit"
            onClick={queue}
            disabled={!draft.probe && !draft.category && !draft.instruction.trim()}
            className="btn-stark"
          >
            Queue
          </button>
        </div>

        <label
          className="mt-4 flex max-w-xl cursor-pointer items-start gap-3"
          htmlFor="persist-order"
        >
          <input
            id="persist-order"
            data-testid="admin-directive-persist"
            type="checkbox"
            checked={!!draft.persist}
            disabled={!draft.probe}
            onChange={(e) => setDraft({ ...draft, persist: e.target.checked })}
            className="mt-[3px] accent-zinc-100"
          />
          <span className="font-data text-[10px] leading-relaxed text-zinc-500">
            STANDING ORDER — hold this until the probe actually fires, instead of expiring after one
            turn. Requires a probe type.
          </span>
        </label>

        <div className="mt-5">
          <p className="label">Pending · {pending.length}</p>
          <ul className="mt-3 space-y-2">
            {pending.map((d) => (
              <li
                key={d.id}
                className="flex items-start gap-3 border border-[var(--line)] px-4 py-3"
                data-testid="admin-directive-pending-item"
              >
                <div className="min-w-0">
                  <p className="font-data text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                    {d.probe || "any probe"} · {d.category || "any category"} ·{" "}
                    {d.issued_at?.slice(11, 19)}
                    {d.persist ? " · STANDING" : ""}
                    {d.origin ? ` · ${d.origin}` : ""}
                  </p>
                  {d.instruction && (
                    <p className="mt-1 text-xs text-zinc-200">{d.instruction}</p>
                  )}
                </div>
                <button
                  onClick={() => cancelDirective(d.id, subject).then(reload)}
                  data-testid="admin-directive-cancel"
                  className="ml-auto text-zinc-600 transition-colors hover:text-zinc-100"
                >
                  <X size={13} strokeWidth={1.8} />
                </button>
              </li>
            ))}
            {pending.length === 0 && (
              <li className="font-data text-[11px] text-zinc-600">
                None. The Cartographer is choosing its own questions.
              </li>
            )}
          </ul>
        </div>
      </section>

      <div className="hair" />

      {/* ---------------------------------------------------- unlock tuning */}
      <section>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <p className="label">Unlock tuning</p>
          {fc && (
            <span className="font-data text-[10px] text-zinc-600">
              {fc.turns_remaining} turns needed · {fc.turns_affordable ?? "—"} affordable in the
              window{" "}
              <span className={fc.fits_in_window ? "text-zinc-300" : "text-zinc-100"}>
                {fc.fits_in_window === null ? "" : fc.fits_in_window ? "· FITS" : "· WILL NOT FIT"}
              </span>
            </span>
          )}
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {TUNE_FIELDS.map(([key, label, hint]) => {
            const [lo, hi] = tune?.bounds?.[key] || [0, 100];
            return (
              <div key={key} className="border border-[var(--line)] px-4 py-4">
                <div className="flex items-baseline justify-between">
                  <p className="label">{label}</p>
                  <span className="font-data text-sm tabular-nums text-zinc-100">
                    {tdraft?.[key] ?? "—"}
                  </span>
                </div>
                <input
                  type="range"
                  data-testid={`admin-tuning-${key}`}
                  min={lo}
                  max={hi}
                  step={key === "evidence_saturation" ? 1 : 1}
                  value={tdraft?.[key] ?? lo}
                  onChange={(e) => setTdraft({ ...tdraft, [key]: Number(e.target.value) })}
                  onMouseUp={(e) => saveTuning({ [key]: Number(e.target.value) })}
                  onTouchEnd={(e) => saveTuning({ [key]: Number(e.target.value) })}
                  className="mt-3 w-full accent-zinc-100"
                />
                <p className="mt-2 font-data text-[10px] text-zinc-600">
                  {hint} · default {tune?.defaults?.[key]} · range {lo}–{hi}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            data-testid="admin-tuning-fit-btn"
            disabled={!fc?.recommended_saturation}
            onClick={() => saveTuning({ evidence_saturation: fc.recommended_saturation })}
            className="btn-stark"
          >
            Fit to the remaining window
            {fc?.recommended_saturation ? ` (${fc.recommended_saturation})` : ""}
          </button>
          <button
            data-testid="admin-tuning-reset-btn"
            onClick={() => saveTuning(tune?.defaults || {})}
            className="label hover:text-zinc-200"
          >
            restore defaults
          </button>
          {fc?.seconds_per_turn && (
            <span className="font-data text-[10px] text-zinc-600">
              observed {fc.seconds_per_turn}s per turn
            </span>
          )}
        </div>
      </section>

      {msg && (
        <p data-testid="admin-control-msg" className="font-data text-[11px] text-zinc-300">
          — {msg}
        </p>
      )}
    </div>
  );
};

export default OperatorControls;
